/**
 * HubTour — the Client Hub's "Getting started" checklist and its click-through walkthroughs
 * (docs/CLIENT_HUB_TOUR.md, Matt 2026-10-26).
 *
 * Five steps, each a short walkthrough that spotlights the real control on the page (elements
 * carry data-tour="…" in JourneyShell.jsx / JourneyPage.jsx). Finishing a walkthrough ticks its
 * step, except inspiration: that ticks when the person adds their first picture to the board (the
 * server derives it), or when they choose "We'll skip this one".
 *
 * Where it sits, so it never covers the Hub's own controls:
 *   - Wide screens: a small "Getting started" pill in the lower right, in the band beside Back/Next
 *     (nothing else lives in that corner). The list opens upward from it; it opens by itself only on
 *     the person's first visit. Its z-index sits UNDER every full-screen panel (invoices,
 *     agreements, signing, presentations, drawing sets), so those cover it while open.
 *   - Phones (< 760px, where the carousel shows one card edge to edge): nothing floats. A
 *     "Getting started" row sits at the top of the Roadmap drawer and opens the list as a sheet; the
 *     sheet opens by itself on the first visit.
 *
 * Progress is per person (journey_recipients.tour), saved through services/journey.js#saveTourStep.
 * A preview pass saves nothing (the ticks live in the page); a legacy shared link with no recipient
 * keeps its ticks in this browser.
 */

import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { saveTourStep, getJourney, isPreviewToken } from '../services/journey.js'

const NARROW = 760
const STEPS = [
  { key: 'entries',     title: 'How updates work',   hint: 'Each card is one update from your team' },
  { key: 'roadmap',     title: 'Your Roadmap',       hint: 'Every update, grouped by project phase' },
  { key: 'updates',     title: 'Email updates',      hint: 'A short note when something new is posted' },
  { key: 'invoices',    title: 'Your invoices',      hint: 'See, download and pay them' },
  { key: 'inspiration', title: 'Inspiration images', hint: 'Show us the homes and rooms you love' },
]

function lsGet(key) { try { return JSON.parse(localStorage.getItem(key) || 'null') } catch { return null } }
function lsSet(key, v) { try { localStorage.setItem(key, JSON.stringify(v)) } catch { /* private mode */ } }

// The walkthrough for one step: a list of coach marks. `target` is a data-tour name; `drawer` opens
// (true) or closes (false) the Roadmap first.
function coachFor(step, ctx) {
  if (step === 'entries') return [
    { target: 'entry-card', drawer: false, title: 'Updates from your team',
      body: 'Each card is one update from us: a document to read, sketches to look at, a question for you, or a step we have finished. Scroll inside a card to see all of it.' },
    { target: 'entry-nav', drawer: false, title: 'Moving between updates',
      body: 'Use Back and Next, or the arrow keys on your keyboard. The number shows where you are. The Hub opens on the newest update you have not seen yet.' },
  ]
  if (step === 'roadmap') return [
    { target: 'roadmap', drawer: false, title: 'The Roadmap',
      body: 'This opens your Roadmap: the list of every update in your project.' },
    { target: 'roadmap-list', drawer: true, title: 'Every update, by phase',
      body: 'Updates are grouped by project phase. Search, or tap one to jump straight to it. A blue New tag marks anything posted since your last visit, and a check mark means you have already responded.' },
  ]
  if (step === 'updates') return [
    { target: 'updates', drawer: true, title: 'Email updates',
      body: 'We send you a short note by email whenever something new is posted. Nothing more, and every email has an unsubscribe link. Anyone else who wants the notes can add their email here.' },
  ]
  if (step === 'invoices') return [
    ctx.hasInvoices
      ? { target: 'invoices', drawer: true, title: 'Your invoices',
          body: 'Your invoices live here. Open the list to see each one, download a PDF, or pay online by bank transfer or card.' }
      : { target: ['shortcuts', 'roadmap-drawer'], drawer: true, title: 'Your invoices',
          body: 'When we send your first invoice, an Invoices link appears here in your Roadmap. You will be able to see each invoice, download a PDF, and pay online.' },
  ]
  return [
    { target: ctx.inspirationOpen ? 'inspiration' : null, drawer: false, title: 'Your inspiration board', inspiration: true,
      body: 'Collect pictures of homes, rooms, materials and details you love, and add a note about what caught your eye. When we meet to go over your concept, we look through the board together, so the designs we show you next feel like yours. We may add a few pictures of our own to check we have understood your taste.'
        + (ctx.inspirationOpen ? ' Your first picture checks off this step.' : ' Your board opens once your proposal is signed.') },
  ]
}

export default function HubTour({ token, tour, setTour, project, billingSummary }) {
  const preview = isPreviewToken(token)
  const legacy = !tour && !preview
  const uiKey = 'hub-tour-ui:' + (token || '')
  const legacyKey = 'hub-tour:' + (token || '')

  const [steps, setSteps] = useState(() => (tour && tour.steps) || (legacy ? (lsGet(legacyKey) || {}) : {}))
  const finishedAtLoad = useRef(!!(tour && tour.finishedAt) || !!(legacy && lsGet(uiKey)?.closed))
  const [ui, setUi] = useState(() => (preview ? null : lsGet(uiKey)) || {})
  const firstVisit = !ui.seen && !Object.keys(steps).length
  const [open, setOpen] = useState(firstVisit)          // the list (wide) / the sheet (phone)
  const [running, setRunning] = useState(null)          // { step, i }
  const [winW, setWinW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280)
  const boardOpened = useRef(false)

  // An owner's rep has no invoices, so his tour has no invoices step (the server counts it done).
  const LIST = project?.viewerRole === 'owners_rep' ? STEPS.filter(s => s.key !== 'invoices') : STEPS
  const narrow = winW < NARROW
  const done = LIST.filter(s => steps[s.key]).length
  const allDone = done === LIST.length

  useEffect(() => { if (tour && tour.steps) setSteps(prev => ({ ...prev, ...tour.steps })) }, [tour])
  useEffect(() => {
    const onResize = () => setWinW(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const saveUi = useCallback(patch => {
    setUi(prev => { const next = { ...prev, ...patch }; if (!preview) lsSet(uiKey, next); return next })
  }, [preview, uiKey])
  useEffect(() => { if (!ui.seen) saveUi({ seen: true }) }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function markDone(step, skipped = false) {
    if (steps[step]) return
    const at = new Date().toISOString()
    setSteps(prev => {
      const next = { ...prev, [step]: skipped ? { skipped: true, at } : at }
      if (legacy) lsSet(legacyKey, next)
      return next
    })
    const saved = await saveTourStep(token, step, skipped)
    if (saved) { setSteps(prev => ({ ...prev, ...saved.steps })); if (setTour) setTour(saved) }
  }

  // Back from the inspiration board's tab: the server knows whether they added a picture.
  useEffect(() => {
    function onFocus() {
      if (!boardOpened.current || steps.inspiration || preview || legacy) return
      getJourney(token).then(d => { if (d && d.tour) { setSteps(prev => ({ ...prev, ...d.tour.steps })); if (setTour) setTour(d.tour) } }).catch(() => {})
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [steps.inspiration, token, preview, legacy, setTour])

  function start(step) {
    setOpen(false)
    window.dispatchEvent(new CustomEvent('hub-tour-roadmap', { detail: { open: false } }))
    setRunning({ step, i: 0 })
  }
  function stopRunning() {
    window.dispatchEvent(new CustomEvent('hub-tour-roadmap', { detail: { open: false } }))
    setRunning(null)
  }

  const ctx = { hasInvoices: !!billingSummary?.hasInvoices, inspirationOpen: !!project?.inspirationOpen }
  const marks = running ? coachFor(running.step, ctx) : null
  const mark = marks ? marks[running.i] : null

  // Finished on an earlier visit, or closed after finishing: nothing to show.
  if (finishedAtLoad.current && !running) return null
  if (ui.closed && allDone && !running) return null

  const list = (
    <TourList list={LIST} steps={steps} done={done} allDone={allDone} onStart={start}
      onMinimize={() => setOpen(false)}
      onClose={() => { saveUi({ closed: true }); setOpen(false) }} narrow={narrow} />
  )

  return (
    <>
      {/* Wide: the pill + the list above it */}
      {!narrow && !running && (
        <div style={{ position: 'fixed', right: 24, bottom: 24, zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
          {open && <div style={T.panel}>{list}</div>}
          <button onClick={() => setOpen(o => !o)} style={T.pill} aria-expanded={open}>
            <Ring done={done} total={LIST.length} />
            <span>{allDone ? 'All set' : 'Getting started'}</span>
            <span style={T.pillCount}>{done}/{LIST.length}</span>
          </button>
        </div>
      )}

      {/* Phone: a row in the Roadmap drawer + a bottom sheet */}
      {narrow && <DrawerRow total={LIST.length} done={done} allDone={allDone} onOpen={() => { window.dispatchEvent(new CustomEvent('hub-tour-roadmap', { detail: { open: false } })); setOpen(true) }} />}
      {narrow && open && !running && (
        <>
          <div onClick={() => setOpen(false)} style={T.sheetScrim} />
          <div style={T.sheet}>{list}</div>
        </>
      )}

      {mark && (
        <Coach key={running.step + ':' + running.i} mark={mark} index={running.i} count={marks.length}
          onBack={() => setRunning(r => ({ ...r, i: Math.max(0, r.i - 1) }))}
          onNext={() => {
            if (running.i < marks.length - 1) { setRunning(r => ({ ...r, i: r.i + 1 })); return }
            const step = running.step
            stopRunning()
            if (step !== 'inspiration') markDone(step)
            setOpen(true)
          }}
          onClose={() => { stopRunning(); setOpen(true) }}
          inspirationOpen={ctx.inspirationOpen}
          inspirationDone={!!steps.inspiration}
          onOpenBoard={() => {
            boardOpened.current = true
            window.open('/inspiration?t=' + encodeURIComponent(token || ''), '_blank')
            stopRunning(); setOpen(true)
          }}
          onSkip={() => { stopRunning(); markDone('inspiration', true); setOpen(true) }}
        />
      )}
    </>
  )
}

function TourList({ list: LIST, steps, done, allDone, onStart, onMinimize, onClose, narrow }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div>
          <div style={T.eyebrow}>Getting started</div>
          <div style={T.heading}>{allDone ? 'You are all set' : 'Get to know your Hub'}</div>
        </div>
        <button onClick={allDone ? onClose : onMinimize} style={T.iconBtn} aria-label={allDone ? 'Close' : 'Hide'}>
          {allDone || narrow ? '×' : '–'}
        </button>
      </div>
      <div style={T.bar}><div style={{ ...T.barFill, width: (done / LIST.length * 100) + '%' }} /></div>
      <div style={{ marginTop: 6 }}>
        {LIST.map(s => {
          const v = steps[s.key]
          const skipped = v && typeof v === 'object' && v.skipped
          return (
            <button key={s.key} onClick={() => onStart(s.key)} style={T.row}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(7,39,45,0.04)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}>
              <span style={{ ...T.check, ...(v ? T.checkDone : null) }}>{v ? '✓' : ''}</span>
              <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <span style={{ ...T.rowTitle, ...(v ? { color: '#6b8f95' } : null) }}>{s.title}</span>
                <span style={T.rowHint}>{skipped ? 'Skipped' : s.hint}</span>
              </span>
              <span style={T.chev}>›</span>
            </button>
          )
        })}
      </div>
      {allDone && <div style={T.allDone}>Thanks for taking the tour. You can find all of this again in your Roadmap.</div>}
    </div>
  )
}

function DrawerRow({ total, done, allDone, onOpen }) {
  const [slot, setSlot] = useState(null)
  useEffect(() => { setSlot(document.getElementById('hub-tour-drawer-slot')) }, [])
  if (!slot) return null
  return createPortal(
    <button onClick={onOpen} style={T.drawerRow}>
      <Ring done={done} total={total} light />
      <span style={{ flex: 1, textAlign: 'left' }}>{allDone ? 'Getting started: all set' : 'Getting started'}</span>
      <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 11, opacity: 0.7 }}>{done}/{total}</span>
    </button>,
    slot,
  )
}

function Ring({ done, total, light }) {
  const r = 7, c = 2 * Math.PI * r
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx="9" cy="9" r={r} fill="none" stroke={light ? 'rgba(255,255,255,0.25)' : 'rgba(7,39,45,0.15)'} strokeWidth="2" />
      <circle cx="9" cy="9" r={r} fill="none" stroke={light ? '#fff' : '#1D9E75'} strokeWidth="2"
        strokeDasharray={c} strokeDashoffset={c * (1 - done / (total || 1))} strokeLinecap="round" transform="rotate(-90 9 9)" />
    </svg>
  )
}

// One coach mark: a spotlight around the real control and a card beside it. Clicks elsewhere are
// held while it is up, so the walkthrough cannot drift from what it points at.
function Coach({ mark, index, count, onBack, onNext, onClose, inspirationOpen, inspirationDone, onOpenBoard, onSkip }) {
  const [rect, setRect] = useState(null)
  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight })
  const cardRef = useRef(null)
  const [cardH, setCardH] = useState(190)

  useEffect(() => {
    if (typeof mark.drawer === 'boolean') window.dispatchEvent(new CustomEvent('hub-tour-roadmap', { detail: { open: mark.drawer } }))
  }, [mark])

  // Follow the target while the drawer slides and the page settles.
  useEffect(() => {
    let raf
    const names = Array.isArray(mark.target) ? mark.target : (mark.target ? [mark.target] : [])
    const tick = () => {
      let r = null
      for (const n of names) {
        const el = document.querySelector('[data-tour="' + n + '"]')
        if (el) { const b = el.getBoundingClientRect(); if (b.width > 2 && b.height > 2) { r = b; break } }
      }
      setRect(prev => (prev && r && prev.left === r.left && prev.top === r.top && prev.width === r.width && prev.height === r.height) ? prev : r)
      setVp(prev => (prev.w === window.innerWidth && prev.h === window.innerHeight) ? prev : { w: window.innerWidth, h: window.innerHeight })
      raf = requestAnimationFrame(tick)
    }
    tick()
    return () => cancelAnimationFrame(raf)
  }, [mark])

  useLayoutEffect(() => { if (cardRef.current) setCardH(cardRef.current.offsetHeight) })

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); else if (e.key === 'Enter' && !mark.inspiration) onNext() }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose, onNext, mark])

  const W = Math.min(330, vp.w - 32)
  const pad = 6
  let pos
  if (!rect) {
    pos = { left: (vp.w - W) / 2, top: Math.max(16, (vp.h - cardH) / 2) }
  } else {
    const below = rect.bottom + pad + 12, above = rect.top - pad - 12 - cardH
    const clampX = x => Math.max(16, Math.min(x, vp.w - W - 16))
    if (below + cardH <= vp.h - 12) pos = { left: clampX(rect.left), top: below }
    else if (above >= 12) pos = { left: clampX(rect.left), top: above }
    else if (rect.right + 12 + W <= vp.w - 12) pos = { left: rect.right + 12, top: Math.max(12, Math.min(rect.top, vp.h - cardH - 12)) }
    else if (rect.left - 12 - W >= 12) pos = { left: rect.left - 12 - W, top: Math.max(12, Math.min(rect.top, vp.h - cardH - 12)) }
    else pos = { left: (vp.w - W) / 2, top: vp.h - cardH - 16 }
  }

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 40, background: rect ? 'transparent' : 'rgba(4,21,24,0.6)' }} onClick={e => e.stopPropagation()} />
      {rect && (
        <div style={{
          position: 'fixed', zIndex: 41, pointerEvents: 'none', borderRadius: 10,
          left: rect.left - pad, top: rect.top - pad, width: rect.width + pad * 2, height: rect.height + pad * 2,
          boxShadow: '0 0 0 9999px rgba(4,21,24,0.6)', border: '1.5px solid rgba(255,255,255,0.85)',
          transition: 'left 0.2s ease, top 0.2s ease, width 0.2s ease, height 0.2s ease',
        }} />
      )}
      <div ref={cardRef} role="dialog" aria-label={mark.title} style={{ ...T.coach, width: W, left: pos.left, top: pos.top }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={T.eyebrow}>{count > 1 ? `${index + 1} of ${count}` : 'Getting started'}</div>
          <button onClick={onClose} style={T.iconBtn} aria-label="Close">×</button>
        </div>
        <div style={T.coachTitle}>{mark.title}</div>
        <div style={T.coachBody}>{mark.body}</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
          {mark.inspiration ? (
            <>
              {!inspirationDone && <button onClick={onSkip} style={T.ghostBtn}>We&rsquo;ll skip this one</button>}
              {inspirationOpen
                ? <button onClick={onOpenBoard} style={T.primaryBtn}>Open my board</button>
                : <button onClick={onClose} style={T.primaryBtn}>Got it</button>}
            </>
          ) : (
            <>
              {index > 0 && <button onClick={onBack} style={T.ghostBtn}>Back</button>}
              <button onClick={onNext} style={T.primaryBtn}>{index < count - 1 ? 'Next' : 'Got it'}</button>
            </>
          )}
        </div>
      </div>
    </>
  )
}

const T = {
  pill: {
    display: 'flex', alignItems: 'center', gap: 9, padding: '9px 14px 9px 11px', borderRadius: 20,
    border: '0.5px solid rgba(255,255,255,0.55)', background: 'rgba(253,253,251,0.96)', cursor: 'pointer',
    boxShadow: '0 10px 30px rgba(4,21,24,0.35)', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, color: '#07272D',
  },
  pillCount: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: '#6b8f95', fontWeight: 400 },
  panel: {
    width: 300, maxWidth: 'calc(100vw - 48px)', background: 'rgba(253,253,251,0.98)', borderRadius: 10,
    border: '0.5px solid rgba(255,255,255,0.55)', boxShadow: '0 20px 60px rgba(4,21,24,0.45)', padding: '16px 14px 10px',
  },
  sheetScrim: { position: 'fixed', inset: 0, zIndex: 29, background: 'rgba(4,21,24,0.45)' },
  sheet: {
    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 30, background: 'rgba(253,253,251,0.99)',
    borderRadius: '14px 14px 0 0', padding: '18px 16px 26px', boxShadow: '0 -12px 40px rgba(4,21,24,0.4)',
    maxHeight: '80vh', overflowY: 'auto',
  },
  drawerRow: {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', marginTop: 14, padding: '9px 10px', borderRadius: 8,
    border: '0.5px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)', cursor: 'pointer',
    fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, color: 'rgba(255,255,255,0.92)',
  },
  eyebrow: { fontFamily: '"Montserrat", sans-serif', fontSize: 9, fontWeight: 500, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#7a9aaa' },
  heading: { fontFamily: '"Montserrat", sans-serif', fontSize: 15, fontWeight: 300, letterSpacing: '0.04em', color: '#07272D', marginTop: 5 },
  iconBtn: { background: 'none', border: 'none', fontSize: 18, lineHeight: 1, color: '#9ab8bd', cursor: 'pointer', padding: 2 },
  bar: { height: 3, borderRadius: 2, background: 'rgba(7,39,45,0.08)', marginTop: 12, overflow: 'hidden' },
  barFill: { height: '100%', background: '#1D9E75', transition: 'width 0.4s ease' },
  row: {
    display: 'flex', alignItems: 'center', gap: 11, width: '100%', padding: '9px 6px', borderRadius: 7,
    border: 'none', background: 'transparent', cursor: 'pointer', transition: 'background 0.12s',
  },
  check: {
    width: 20, height: 20, borderRadius: '50%', border: '1.5px solid rgba(7,39,45,0.22)', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 700,
  },
  checkDone: { background: '#1D9E75', borderColor: '#1D9E75' },
  rowTitle: { display: 'block', fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600, color: '#07272D' },
  rowHint: { display: 'block', fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: '#7a9aaa', marginTop: 2 },
  chev: { fontSize: 16, color: '#b6c9cf', flexShrink: 0 },
  allDone: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#5a7884', lineHeight: 1.5, padding: '8px 6px 4px' },
  coach: {
    position: 'fixed', zIndex: 42, background: '#fff', borderRadius: 10, padding: '14px 16px 14px',
    boxShadow: '0 18px 50px rgba(4,21,24,0.45)', transition: 'left 0.2s ease, top 0.2s ease',
  },
  coachTitle: { fontFamily: '"Montserrat", sans-serif', fontSize: 16, fontWeight: 300, letterSpacing: '0.03em', color: '#07272D', marginTop: 6 },
  coachBody: { fontFamily: '"DM Sans", sans-serif', fontSize: 13.5, lineHeight: 1.6, color: '#3a5a60', marginTop: 8 },
  primaryBtn: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600, color: '#fff', background: '#07272D', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer' },
  ghostBtn: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#5a7884', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 6px' },
}
